import { useEffect, useRef, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { ui } from "@/lib/ui";
import { toast } from "sonner";
import {
  Loader2, UploadCloud, FileText, Download, Trash2, Save, ArrowLeft, Pencil, Bold,
  Minus, Plus, AlignLeft, AlignCenter, AlignRight,
} from "lucide-react";

const DISPLAY_W = 820;

const colorHex = (c) => "#" + ((c >>> 0) & 0xffffff).toString(16).padStart(6, "0");
const fontFamily = (flags) =>
  flags & 8 ? "ui-monospace, monospace" : flags & 4 ? "Georgia, 'Times New Roman', serif" : "Helvetica, Arial, sans-serif";

function Span({ span, text, fmt, pos, scale, active, masked, onInput, onCommit, onFocus, onDrag }) {
  const [x0, y0, x1, y1] = span.bbox;
  const bold = fmt?.bold ?? !!(span.flags & 16);
  const size = fmt?.size ?? span.size;
  const align = fmt?.align ?? "left";
  const dx = pos?.dx ?? 0, dy = pos?.dy ?? 0;
  const boxW = Math.max(x1 - x0, 6);
  const boxH = Math.max(y1 - y0, size * 1.3) + 2;

  const startDrag = (e) => {
    e.preventDefault(); e.stopPropagation();
    const sx = e.clientX, sy = e.clientY, bdx = dx, bdy = dy;
    const move = (ev) => onDrag(span.id, bdx + (ev.clientX - sx) / scale, bdy + (ev.clientY - sy) / scale);
    const up = () => { window.removeEventListener("mousemove", move); window.removeEventListener("mouseup", up); };
    window.addEventListener("mousemove", move); window.addEventListener("mouseup", up);
  };

  const edit = {
    position: "absolute", left: 0, top: 0, width: boxW, transform: `translate(${dx}px, ${dy}px)`, zIndex: 20,
    fontSize: size, lineHeight: `${size * 1.15}px`, color: colorHex(span.color), fontFamily: fontFamily(span.flags),
    fontWeight: bold ? 700 : 400, fontStyle: span.flags & 2 ? "italic" : "normal", textAlign: align,
    background: "#ffffff", whiteSpace: "pre", outline: "none", padding: 0, margin: 0, border: "none",
    cursor: "text", minHeight: size * 1.2, overflow: "visible",
  };
  return (
    <div style={{ position: "absolute", left: x0, top: y0, width: boxW }}>
      {/* Mask the ORIGINAL location so the underlying page text disappears once a block
          is edited or dragged — the block then moves like an original Canva element. */}
      {masked && (
        <div data-testid={`doc-mask-${span.id}`}
          style={{ position: "absolute", left: -1, top: -1, width: boxW + 2, height: boxH, background: "#ffffff", zIndex: 10 }} />
      )}
      {active && (
        <div data-testid={`doc-drag-${span.id}`} onMouseDown={startDrag} title="Drag to move"
          style={{ position: "absolute", left: -13, top: -1, width: 11, height: Math.max(size, 11),
            background: "#6366f1", borderRadius: 2, cursor: "move", zIndex: 30, transform: `translate(${dx}px, ${dy}px)` }} />
      )}
      <div contentEditable suppressContentEditableWarning spellCheck={false}
        data-testid={`doc-span-${span.id}`}
        className="doc-span hover:ring-1 hover:ring-indigo-400/60 focus:ring-2 focus:ring-indigo-500 rounded-[1px]"
        style={edit}
        onFocus={() => onFocus(span.id)}
        onInput={(e) => onInput(span.id, e.currentTarget.innerText)}
        onBlur={(e) => onCommit(span.id, e.currentTarget.innerText)}>
        {text}
      </div>
    </div>
  );
}

function Page({ page, edits, formats, pos, activeId, maskedIds, onInput, onCommit, onFocus, onDrag }) {
  const scale = Math.min(1, DISPLAY_W / page.width);
  return (
    <div className="relative mx-auto shadow-xl ring-1 ring-slate-700/50 bg-white"
      style={{ width: page.width * scale, height: page.height * scale }} data-testid={`doc-page-${page.page}`}>
      <div style={{ width: page.width, height: page.height, transform: `scale(${scale})`, transformOrigin: "top left", position: "absolute", top: 0, left: 0 }}>
        <img src={page.image_url} alt={`page ${page.page + 1}`} width={page.width} height={page.height}
          draggable={false} style={{ position: "absolute", top: 0, left: 0, userSelect: "none" }} />
        {page.spans.map((s) => (
          <Span key={s.id} span={s} text={edits[s.id] ?? s.text} fmt={formats[s.id]} pos={pos[s.id]}
            scale={scale} active={activeId === s.id} masked={maskedIds.has(s.id)}
            onInput={onInput} onCommit={onCommit} onFocus={onFocus} onDrag={onDrag} />
        ))}
      </div>
    </div>
  );
}

export default function DocumentEditor() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [active, setActive] = useState(null);
  const [opening, setOpening] = useState(false);
  const [edits, setEdits] = useState({});
  const editsRef = useRef({});
  const [formats, setFormats] = useState({});
  const formatsRef = useRef({});
  const [pos, setPos] = useState({});
  const posRef = useRef({});
  const [activeId, setActiveId] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const load = () =>
    api.get("/documents").then(({ data }) => setDocs(data)).catch(() => toast.error("Failed to load documents")).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const spanMap = {};
  if (active) active.pages.forEach((p) => p.spans.forEach((s) => { spanMap[s.id] = { span: s, page: p.page }; }));

  const upload = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) return toast.error("Please choose a PDF file.");
    setUploading(true);
    try {
      const b64 = await new Promise((res, rej) => {
        const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file);
      });
      const { data } = await api.post("/documents/upload", { filename: file.name, file_base64: b64 });
      toast.success("Document uploaded");
      await load();
      openDoc(data.id);
    } catch (e) { toast.error(apiErr(e)); } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const openDoc = async (id) => {
    setOpening(true);
    try {
      const { data } = await api.get(`/documents/${id}`);
      setActive(data);
      editsRef.current = {}; setEdits({});
      formatsRef.current = {}; setFormats({});
      posRef.current = {}; setPos({});
      setActiveId(null);
    } catch (e) { toast.error(apiErr(e)); } finally { setOpening(false); }
  };

  const origText = (id) => spanMap[id]?.span.text ?? null;
  const onSpanInput = (id, text) => {
    const o = origText(id); if (o == null) return;
    if (text === o) delete editsRef.current[id]; else editsRef.current[id] = text;
  };
  const onSpanCommit = (id, text) => { onSpanInput(id, text); setEdits({ ...editsRef.current }); };

  const setFmt = (id, patch) => {
    formatsRef.current = { ...formatsRef.current, [id]: { ...(formatsRef.current[id] || {}), ...patch } };
    setFormats({ ...formatsRef.current });
  };
  const applyFmt = (patch) => {
    if (!activeId) return;
    const el = document.querySelector(`[data-testid="doc-span-${activeId}"]`);
    if (el) onSpanInput(activeId, el.innerText);
    setEdits({ ...editsRef.current });
    setFmt(activeId, patch);
  };
  const onDrag = (id, dx, dy) => {
    posRef.current = { ...posRef.current, [id]: { dx, dy } };
    setPos({ ...posRef.current });
  };

  const fmtChanged = (id) => {
    const s = spanMap[id]?.span; if (!s) return false;
    const f = formats[id] || {};
    const bc = f.bold !== undefined && f.bold !== !!(s.flags & 16);
    const sc = f.size !== undefined && Math.round(f.size) !== Math.round(s.size);
    const ac = f.align !== undefined && f.align !== "left";
    return bc || sc || ac;
  };
  const posChanged = (id) => { const p = pos[id]; return p && (Math.abs(p.dx || 0) > 0.5 || Math.abs(p.dy || 0) > 0.5); };
  const changedIds = new Set([
    ...Object.keys(edits),
    ...Object.keys(formats).filter(fmtChanged),
    ...Object.keys(pos).filter(posChanged),
  ]);
  const changedCount = changedIds.size;

  const activeSpan = activeId ? spanMap[activeId]?.span : null;
  const effBold = activeSpan ? (formats[activeId]?.bold ?? !!(activeSpan.flags & 16)) : false;
  const effSize = activeSpan ? Math.round((formats[activeId]?.size ?? activeSpan.size) * 10) / 10 : 0;
  const effAlign = activeSpan ? (formats[activeId]?.align ?? "left") : "left";

  const save = async () => {
    if (!changedIds.size) return;
    setSaving(true);
    try {
      const payload = [...changedIds].map((id) => {
        const { span, page } = spanMap[id];
        const f = formatsRef.current[id] || {};
        const p = posRef.current[id] || {};
        return {
          page, span_id: id,
          text: editsRef.current[id] ?? span.text,
          bold: f.bold ?? null, size: f.size ?? null, align: f.align ?? null,
          dx: p.dx ?? null, dy: p.dy ?? null,
        };
      });
      await api.put(`/documents/${active.id}`, { edits: payload });
      toast.success("Changes saved to the document");
      await openDoc(active.id);
      load();
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  const download = async () => {
    try {
      const res = await api.get(`/documents/${active.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = active.filename.endsWith(".pdf") ? active.filename : active.filename + ".pdf";
      a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const remove = async (d) => {
    if (!window.confirm(`Delete "${d.filename}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/documents/${d.id}`);
      toast.success("Deleted");
      if (active?.id === d.id) setActive(null);
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  // ---------- Editor view ----------
  if (active) {
    const tb = "p-2 rounded-md border border-slate-700 text-slate-300 hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors";
    const act = "bg-indigo-600/30 border-indigo-500 text-indigo-200";
    const alignBtn = (val, Icon, tid) => (
      <button className={`${tb} ${effAlign === val ? act : ""}`} disabled={!activeId} data-testid={tid}
        onMouseDown={(e) => e.preventDefault()} onClick={() => applyFmt({ align: val })} title={`Align ${val}`}>
        <Icon className="w-4 h-4" />
      </button>
    );
    return (
      <div className="space-y-4 animate-fade-up" data-testid="document-editor-view">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <button onClick={() => setActive(null)} data-testid="doc-back-btn" className={ui.btnGhost}>
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
            <div className="min-w-0">
              <h1 className="text-xl font-extrabold tracking-tight text-slate-100 font-h truncate">{active.filename}</h1>
              <p className="text-xs text-slate-500">Click text to edit · drag the blue handle to move · use the toolbar to style &amp; align</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={save} disabled={!changedCount || saving} data-testid="doc-save-btn" className={ui.btnPrimary}>
              {saving ? <Loader2 className="w-4 h-4 spin" /> : <Save className="w-4 h-4" />}
              {changedCount ? `Save ${changedCount} change${changedCount > 1 ? "s" : ""}` : "Saved"}
            </button>
            <button onClick={download} data-testid="doc-download-btn" className={ui.btnGhost}>
              <Download className="w-4 h-4" /> Download PDF
            </button>
          </div>
        </div>

        <div className="sticky top-0 z-20 flex items-center gap-2 bg-slate-900/90 backdrop-blur border border-slate-800 rounded-lg px-3 py-2 flex-wrap" data-testid="doc-format-toolbar">
          <span className="text-[11px] font-mono uppercase tracking-widest text-slate-500 mr-1">Format</span>
          <button className={`${tb} ${effBold ? act : ""}`} disabled={!activeId} data-testid="format-bold-btn"
            onMouseDown={(e) => e.preventDefault()} onClick={() => applyFmt({ bold: !effBold })} title="Bold">
            <Bold className="w-4 h-4" />
          </button>
          <div className="w-px h-6 bg-slate-700 mx-1" />
          {alignBtn("left", AlignLeft, "format-align-left")}
          {alignBtn("center", AlignCenter, "format-align-center")}
          {alignBtn("right", AlignRight, "format-align-right")}
          <div className="w-px h-6 bg-slate-700 mx-1" />
          <button className={tb} disabled={!activeId} data-testid="format-size-dec"
            onMouseDown={(e) => e.preventDefault()} onClick={() => applyFmt({ size: Math.max(5, Math.round(effSize) - 1) })} title="Smaller">
            <Minus className="w-4 h-4" />
          </button>
          <span className="text-xs text-slate-300 tabular-nums w-10 text-center" data-testid="format-size-value">{activeId ? `${effSize}pt` : "—"}</span>
          <button className={tb} disabled={!activeId} data-testid="format-size-inc"
            onMouseDown={(e) => e.preventDefault()} onClick={() => applyFmt({ size: Math.round(effSize) + 1 })} title="Larger">
            <Plus className="w-4 h-4" />
          </button>
          <span className="text-xs text-slate-500 ml-2">{activeId ? "Editing selected text" : "Select a text block to format it"}</span>
          {changedCount > 0 && (
            <span className="ml-auto text-xs text-amber-300 inline-flex items-center gap-1" data-testid="doc-unsaved-note">
              <Pencil className="w-3.5 h-3.5" /> {changedCount} unsaved
            </span>
          )}
        </div>

        {opening ? (
          <div className="flex items-center gap-2 text-slate-500"><Loader2 className="w-4 h-4 spin" /> Loading pages…</div>
        ) : (
          <div className="space-y-8 pb-10">
            {active.pages.map((p) => (
              <Page key={p.page} page={p} edits={edits} formats={formats} pos={pos} activeId={activeId}
                maskedIds={new Set([...changedIds, ...(activeId ? [activeId] : [])])}
                onInput={onSpanInput} onCommit={onSpanCommit} onFocus={setActiveId} onDrag={onDrag} />
            ))}
          </div>
        )}
      </div>
    );
  }

  // ---------- Library view ----------
  return (
    <div className="space-y-6 animate-fade-up" data-testid="documents-page">
      <div>
        <h1 className={ui.pageHeader}>Documentation</h1>
        <p className={ui.muted}>Upload a PDF and edit its text inline — bold, align, resize and drag blocks freely while the layout stays intact.</p>
      </div>

      <label className="block border-2 border-dashed border-slate-700 hover:border-indigo-500/60 rounded-xl p-10 text-center cursor-pointer transition-colors bg-slate-900/40"
        data-testid="doc-dropzone" onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); upload(e.dataTransfer.files?.[0]); }}>
        <input ref={fileRef} type="file" accept="application/pdf,.pdf" className="hidden" data-testid="doc-file-input"
          onChange={(e) => upload(e.target.files?.[0])} />
        {uploading ? (
          <div className="flex items-center justify-center gap-2 text-indigo-300"><Loader2 className="w-5 h-5 spin" /> Uploading & parsing…</div>
        ) : (
          <>
            <UploadCloud className="w-8 h-8 text-indigo-400 mx-auto mb-2" />
            <div className="text-sm text-slate-200 font-medium">Drop a PDF here or click to upload</div>
            <div className="text-xs text-slate-500 mt-1">Up to 12 MB · 30 pages</div>
          </>
        )}
      </label>

      <div>
        <h2 className={`${ui.sectionHeader} mb-3`}>Your documents</h2>
        {loading ? (
          <div className="flex items-center gap-2 text-slate-500"><Loader2 className="w-4 h-4 spin" /> Loading…</div>
        ) : docs.length === 0 ? (
          <div className={`${ui.card} p-8 text-center text-slate-500 text-sm`}>No documents yet. Upload a PDF to get started.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {docs.map((d) => (
              <div key={d.id} className={`${ui.card} p-4 flex flex-col gap-3`} data-testid={`doc-card-${d.id}`}>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-indigo-500/15 flex items-center justify-center shrink-0">
                    <FileText className="w-5 h-5 text-indigo-300" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm text-slate-100 font-medium truncate" title={d.filename}>{d.filename}</div>
                    <div className="text-xs text-slate-500">{d.pages} page{d.pages > 1 ? "s" : ""}{d.edited ? " · edited" : ""}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-auto">
                  <button onClick={() => openDoc(d.id)} disabled={opening} data-testid={`doc-open-${d.id}`} className={`${ui.btnPrimary} flex-1`}>
                    <Pencil className="w-4 h-4" /> Edit
                  </button>
                  <button onClick={() => remove(d)} data-testid={`doc-delete-${d.id}`} className="p-2.5 text-slate-500 hover:text-rose-400 transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
